import { api } from '@/lib/api'
import { newIdempotencyKey } from '@/lib/idempotency'
import { z } from 'zod'

export const residentSchema = z.object({
  id: z.string(),
  facility_id: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  mrn: z.string().nullable(),
  dob: z.string().nullable(), // ISO date string
  room: z.string().nullable(),
  pcp_id: z.string().nullable(),
  status: z.enum(['active', 'discharged', 'deceased', 'transferred']),
  source: z.enum(['manual', 'csv', 'ehr']),
  sex: z.enum(['male', 'female', 'other', 'unknown']).nullish().default(null),
  phone: z.string().nullish().default(null),
  pcp_name: z.string().nullish().default(null),
  pcp_fax: z.string().nullish().default(null),
  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
})
export type Resident = z.infer<typeof residentSchema>

export interface ListResidentsParams {
  facility_id?: string
  status?: 'active' | 'discharged' | 'deceased' | 'transferred'
  q?: string
  page?: number
  page_size?: number
}

export async function listResidents(params: ListResidentsParams = {}): Promise<Resident[]> {
  // Convert the typed interface to the loose Record shape the api client
  // expects. Avoids `as` casts threading through TS strict mode.
  const queryParams: Record<string, string | number | boolean | undefined> = { ...params }
  const raw = await api.get<unknown[]>('/residents', { params: queryParams })
  return raw.map((r) => residentSchema.parse(r))
}

export interface CreateResidentInput {
  facility_id: string
  first_name: string
  last_name: string
  mrn?: string | null
  dob?: string | null
  room?: string | null
  pcp_id?: string | null
  notes?: string | null
  sex?: 'male' | 'female' | 'other' | 'unknown' | null
  phone?: string | null
  pcp_name?: string | null
  pcp_fax?: string | null
}

export async function createResident(input: CreateResidentInput): Promise<Resident> {
  const raw = await api.post<unknown>('/residents', input as unknown as Record<string, unknown>, {
    idempotencyKey: newIdempotencyKey(),
  })
  return residentSchema.parse(raw)
}

export interface BulkImportRow {
  first_name: string
  last_name: string
  mrn?: string | null
  dob?: string | null
  room?: string | null
  sex?: 'male' | 'female' | 'other' | 'unknown' | null
  phone?: string | null
  pcp_name?: string | null
  pcp_fax?: string | null
}

export interface BulkImportResult {
  created: number
  errors: { row: number; message: string }[]
}

export async function bulkImportResidents(
  facilityId: string,
  rows: BulkImportRow[],
): Promise<BulkImportResult> {
  return api.post<BulkImportResult>(
    '/residents/bulk',
    { facility_id: facilityId, rows } as unknown as Record<string, unknown>,
    { idempotencyKey: newIdempotencyKey() },
  )
}

export async function updateResident(
  id: string,
  patch: Partial<CreateResidentInput> & { status?: Resident['status'] },
): Promise<Resident> {
  const raw = await api.patch<unknown>(`/residents/${id}`, patch as unknown as Record<string, unknown>)
  return residentSchema.parse(raw)
}

export async function deleteResident(id: string): Promise<void> {
  await api.delete<void>(`/residents/${id}`)
}
